//
// File: User.ts
// Description: Abstract class for user authentication.
// This class is used to provide a common interface for user authentication.
// See CognitoUser for an example implementation of this class.
// Copyright: Broadlume 2024
//

import { UserAttributes } from './UserAttributes';
import { AuthChallengeName } from './AuthChallengeName';
import { AuthChallengeCallback } from './AuthChallengeCallback';

/**
 * Abstract class for user authentication.
 */
export abstract class User {
  /**
   * Resumes the user's session with the authentication service
   */
  abstract resumeSession(): Promise<void>;
  /**
   * Send a sign in request to the authentication service
   * @param username - The username of the user
   * @param password - The users password
   */
  abstract signIn(username: string, password: string): Promise<void>;
  /**
   * Send a sign out request to the authentication service
   */
  abstract signOut(): Promise<void>;
  /**
   * Clear any stored session information from the local browser storage
   */
  abstract clearStoredSessionInfo(): Promise<void>;
  /**
   * Send a switch tenant request to the blx api
   * @param tenantId - The id of the tenant to switch to
   */
  abstract switchTenant(tenantId: string): Promise<void>;
  /**
   * Refresh the user's session with the authentication service.
   * @param force - When true, always contact the auth service. When false (default),
   *                skip the network call if the current session is still valid.
   *                Callers that need fresh claims (tenant switch, 401 retry) must pass true.
   */
  abstract refreshSession(force?: boolean): Promise<void>;
  /**
   * Get the current authentication challenge state
   */
  abstract get authChallengeState(): AuthChallengeName;
  /**
   * Complate a new password challenge flow with the authentication service
   * @param password - The new password
   * @param attrbutes - The required attributes
   * @param fullFormatPhone - Optional phone number in full format (e.g. AD-+376-22322222) for database storage
   */
  abstract completeNewPasswordChallenge(
    password: string,
    attrbutes: Record<string, string>,
    fullFormatPhone?: Record<string, string>,
  ): Promise<void>;
  /**
   * Send a request for a verification code to the authentication service
   * @param attrbuteName - The name of the attribute to request a verification code for
   */
  abstract requestVerificationCode(attrbuteName: string): Promise<void>;
  /**
   * Verify an attribute with the authentication service
   * @param attrbuteName - The name of the attribute to verify
   * @param code - The verification code
   */
  abstract veryfyAttribute(attrbuteName: string, code: string): Promise<void>;
  /**
   * Send a forgot password request to the authentication service. Which should send
   * a reset password code to the user's email
   * @param username - Username of the user to reset the password for
   */
  abstract forgotPassword(username: string): Promise<void>;
  /**
   * Confirm a forgot password flow, setting a new password and verifying the code
   * @param username - The username of the user
   * @param password - The new password
   * @param code - The verification code
   */
  abstract confirmForgotPassword(username: string, password: string, code: string): Promise<void>;
  /**
   * Send a change password request to the authentication service
   * @param oldPassword - The user's current password
   * @param newPassword - The new password
   */
  abstract changePassword(oldPassword: string, newPassword: string): Promise<void>;
  /**
   * Get the user's local attributes
   * @returns The user's local attributes
   */
  abstract get attributes(): UserAttributes;
  /**
   * Set the user's local attributes
   * @param attributes - User attributes to set
   */
  abstract set attributes(attributes: UserAttributes);
  /**
   * Update the user's attributes with the authentication service.
   * Sync the local copy to the authentication service.
   */
  abstract updateUserAttributes(): Promise<void>;

  /**
   * Get the user's id token
   * @returns The user's id token string, null if not authenticated
   */
  abstract get idToken(): string | null;
  /**
   * Get the user's access token
   * @returns The user's access token string, null if not authenticated
   */
  abstract get accessToken(): string | null;
  /**
   * Get the user's refresh token
   * @returns The user's refresh token string, null if not authenticated
   */
  abstract get refreshToken(): string | null;
  /**
   * Get the user's jwt token. This will return the token used in authorization headers
   * @returns The user's jwt token string, null if not authenticated
   */
  abstract get jwtToken(): string | null;
  /**
   * Is the user authenticated
   * @returns True if the user is authenticated, false otherwise
   */
  abstract get isAuthenticated(): boolean;

  /**
   * Does the user have a valid refresh token available
   * @returns True if a refresh token exists, false otherwise
   */
  abstract get hasRefreshToken(): boolean;

  /**
   * Set the authentication challenge callback
   */
  abstract set authChallengeCallback(callback: AuthChallengeCallback);
}
